import axios from "axios";
import Cookies from "js-cookie";

// Constants
const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://api.ighota.org";
const TOKEN_COOKIE_NAME = "ighota_auth_token";

// Create axios instance
const apiClient = axios.create({
	baseURL: API_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
	(config) => {
		const token = Cookies.get(TOKEN_COOKIE_NAME);
		if (token) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		// Handle 401 Unauthorized errors (token expired)
		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			try {
				// Try to refresh token (implementation depends on your backend)
				const refreshToken = Cookies.get("ighota_refresh_token");
				if (refreshToken) {
					const response = await axios.post(
						`${API_URL}/auth/refresh-token`,
						{
							refreshToken,
						}
					);

					const { token } = response.data;
					Cookies.set(TOKEN_COOKIE_NAME, token);

					// Retry the original request with the new token
					if (originalRequest.headers) {
						originalRequest.headers.Authorization = `Bearer ${token}`;
					}
					return axios(originalRequest);
				}
			} catch (refreshError) {
				// If refresh token fails, log out the user
				Cookies.remove(TOKEN_COOKIE_NAME);
				Cookies.remove("ighota_refresh_token");
				window.location.href = "/auth/login?session=expired";
			}
		}

		return Promise.reject(error);
	}
);

// // // API utility functions
export const api = {
	get: (url, config) => apiClient.get(url, config).then((res) => res.data),

	post: (url, data, config) =>
		apiClient.post(url, data, config).then((res) => res.data),

	put: (url, data, config) =>
		apiClient.put(url, data, config).then((res) => res.data),

	patch: (url, data, config) =>
		apiClient.patch(url, data, config).then((res) => res.data),

	delete: (url, config) =>
		apiClient.delete(url, config).then((res) => res.data),
};

// Auth specific API functions
export const authApi = {
	login: async (email, password) => {
		try {
			const response = await api.post("/auth/login", {
				email,
				password,
			});

			Cookies.set(TOKEN_COOKIE_NAME, response.token);
			Cookies.set("ighota_refresh_token", response.refreshToken);

			return response;
		} catch (error) {
			throw error;
		}
	},

	register: async (userData) => {
		try {
			const response = await api.post("/auth/register", userData);

			Cookies.set(TOKEN_COOKIE_NAME, response.token);
			Cookies.set("ighota_refresh_token", response.refreshToken);

			return response;
		} catch (error) {
			throw error;
		}
	},

	logout: () => {
		Cookies.remove(TOKEN_COOKIE_NAME);
		Cookies.remove("ighota_refresh_token");
	},

	getCurrentUser: () => api.get("/auth/me"),

	forgotPassword: (email) => api.post("/auth/forgot-password", { email }),

	resetPassword: (token, password) =>
		api.post("/auth/reset-password", { token, password }),

	verifyEmail: (token) => api.post("/auth/verify-email", { token }),

	resendVerificationEmail: (email) =>
		api.post("/auth/resend-verification", { email }),
};

// Flashcard API functions
export const flashcardApi = {
	getFlashcardSets: (courseId) =>
		api.get(`/courses/${courseId}/flashcard-sets`),

	getFlashcardSet: (courseId, setId) =>
		api.get(`/courses/${courseId}/flashcard-sets/${setId}`),

	createFlashcardSet: (courseId, data) =>
		api.post(`/courses/${courseId}/flashcard-sets`, data),

	updateFlashcardSet: (courseId, setId, data) =>
		api.put(`/courses/${courseId}/flashcard-sets/${setId}`, data),

	deleteFlashcardSet: (courseId, setId) =>
		api.delete(`/courses/${courseId}/flashcard-sets/${setId}`),

	createFlashcard: (courseId, setId, data) =>
		api.post(`/courses/${courseId}/flashcard-sets/${setId}/cards`, data),

	updateFlashcard: (courseId, setId, cardId, data) =>
		api.put(
			`/courses/${courseId}/flashcard-sets/${setId}/cards/${cardId}`,
			data
		),

	deleteFlashcard: (courseId, setId, cardId) =>
		api.delete(
			`/courses/${courseId}/flashcard-sets/${setId}/cards/${cardId}`
		),

	updateFlashcardProgress: (courseId, setId, cardId, difficulty) =>
		api.post(
			`/courses/${courseId}/flashcard-sets/${setId}/cards/${cardId}/progress`,
			{ difficulty }
		),
};

export default apiClient;
